import React, { useState } from 'react';
import { Container, Navbar, Nav, Row, Col, Form, Card, ProgressBar } from 'react-bootstrap';
import './App.css'; 

function App() {
  const [sleep, setSleep] = useState(7);
  const [study, setStudy] = useState(4);
  const [screenTime, setScreenTime] = useState(3);
  const [exercise, setExercise] = useState(1);

  const calculateScores = () => {
    // Weighted calculation for more dynamic scores
    let stress = 0;
    let productivity = 0;
    let health = 0;

    // Stress Calculation
    stress += screenTime * 10; // High screen time increases stress
    stress += (10 - sleep) * 8; // Less sleep increases stress
    stress -= exercise * 15; // Exercise reduces stress
    stress -= study * 2; // Studying (moderate) can reduce stress, but too much can increase

    // Productivity Calculation
    productivity += study * 15;
    productivity += sleep * 5; // Good sleep improves productivity
    productivity -= screenTime * 5; // High screen time reduces productivity
    productivity += exercise * 5; // Exercise can boost productivity

    // Health Calculation
    health += sleep * 15;
    health += exercise * 20;
    health -= screenTime * 8; // High screen time reduces health
    health += study * 2; // Moderate study can contribute to mental health

    // Normalize scores to 0-100 and ensure they don't go below 0 or above 100
    stress = Math.max(0, Math.min(100, stress));
    productivity = Math.max(0, Math.min(100, productivity));
    health = Math.max(0, Math.min(100, health));

    return { stress, productivity, health };
  };

  const { stress, productivity, health } = calculateScores();

  const getDynamicMessage = (scoreName, score) => {
    if (scoreName === 'stress') {
      if (score > 80) return "High stress levels! Critical. Prioritize rest and breaks.";
      if (score > 60) return "Elevated stress. Consider reducing screen time or increasing sleep/exercise.";
      if (score > 40) return "Moderate stress. Keep an eye on your balance.";
      return "Low stress. Great job maintaining balance!";
    }
    if (scoreName === 'productivity') {
      if (score > 80) return "Outstanding productivity! You're making excellent progress.";
      if (score > 60) return "High productivity. You're doing well, stay focused.";
      if (score > 40) return "Moderate productivity. There's room for improvement.";
      return "Low productivity. Review your habits, more sleep or less screen time might help.";
    }
    if (scoreName === 'health') {
      if (score > 80) return "Excellent health! You're thriving.";
      if (score > 60) return "Good health. Keep up the positive routines.";
      if (score > 40) return "Moderate health. Focus on consistent sleep and exercise.";
      return "Health needs attention. Implement healthier habits for better well-being.";
    }
    return "";
  };

  const getVariant = (score) => {
    if (score > 80) return "success";
    if (score > 60) return "info";
    if (score > 40) return "warning";
    return "danger";
  };


  return (
    <div className="App">
      <Navbar bg="primary" variant="dark" expand="lg" className="mb-4">
        <Container>
          <Navbar.Brand href="#home">Student Life Simulator</Navbar.Brand>
          <Navbar.Toggle aria-controls="basic-navbar-nav" />
          <Navbar.Collapse id="basic-navbar-nav">
            <Nav className="me-auto">
            </Nav>
          </Navbar.Collapse>
        </Container>
      </Navbar>

      <Container>
        <Row className="mb-4 justify-content-center">
          <Col md={8} lg={6}>
            <Card className="shadow-sm">
              <Card.Body>
                <Card.Title className="text-center mb-4">Adjust Your Day</Card.Title>
                <Form>
                  <Form.Group className="mb-3">
                    <Form.Label>Sleep (hours): <span className="fw-bold">{sleep}</span></Form.Label>
                    <Form.Range
                      min="0"
                      max="12"
                      value={sleep}
                      onChange={(e) => setSleep(e.target.value)}
                    />
                  </Form.Group>

                  <Form.Group className="mb-3">
                    <Form.Label>Study (hours): <span className="fw-bold">{study}</span></Form.Label>
                    <Form.Range
                      min="0"
                      max="10"
                      value={study}
                      onChange={(e) => setStudy(e.target.value)}
                    />
                  </Form.Group>

                  <Form.Group className="mb-3">
                    <Form.Label>Screen Time (hours): <span className="fw-bold">{screenTime}</span></Form.Label>
                    <Form.Range
                      min="0"
                      max="10"
                      value={screenTime}
                      onChange={(e) => setScreenTime(e.target.value)}
                    />
                  </Form.Group>

                  <Form.Group className="mb-3">
                    <Form.Label>Exercise (hours): <span className="fw-bold">{exercise}</span></Form.Label>
                    <Form.Range
                      min="0"
                      max="4"
                      value={exercise}
                      onChange={(e) => setExercise(e.target.value)}
                    />
                  </Form.Group>
                </Form>
              </Card.Body>
            </Card>
          </Col>
        </Row>

        <Row className="justify-content-center">
          <Col md={10} lg={8}>
            <h2 className="text-center mb-4">Your Scores</h2>
            <Row>
              <Col md={4} className="mb-4">
                <Card className="text-center shadow-sm h-100">
                  <Card.Body>
                    <Card.Title>Stress</Card.Title>
                    <h3 className="mb-3">{stress.toFixed(1)}/100</h3>
                    <ProgressBar now={stress} variant={getVariant(100 - stress)} className="mb-3" />
                    <Card.Text>{getDynamicMessage('stress', stress)}</Card.Text>
                  </Card.Body>
                </Card>
              </Col>
              <Col md={4} className="mb-4">
                <Card className="text-center shadow-sm h-100">
                  <Card.Body>
                    <Card.Title>Productivity</Card.Title>
                    <h3 className="mb-3">{productivity.toFixed(1)}/100</h3>
                    <ProgressBar now={productivity} variant={getVariant(productivity)} className="mb-3" />
                    <Card.Text>{getDynamicMessage('productivity', productivity)}</Card.Text>
                  </Card.Body>
                </Card>
              </Col>
              <Col md={4} className="mb-4">
                <Card className="text-center shadow-sm h-100">
                  <Card.Body>
                    <Card.Title>Health</Card.Title>
                    <h3 className="mb-3">{health.toFixed(1)}/100</h3>
                    <ProgressBar now={health} variant={getVariant(health)} className="mb-3" />
                    <Card.Text>{getDynamicMessage('health', health)}</Card.Text>
                  </Card.Body>
                </Card>
              </Col>
            </Row>
          </Col>
        </Row>
      </Container>
    </div>
  );
}

export default App;

